import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ChevronRight, ChevronDown } from "lucide-react";
import { NAV_LINKS, MEGA_CATEGORIES } from "@/lib/data";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

export default function NavBar() {
  const [activeCat, setActiveCat] = useState(MEGA_CATEGORIES[0].slug);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const nav = useNavigate();

  const active = MEGA_CATEGORIES.find((c) => c.slug === activeCat) || MEGA_CATEGORIES[0];

  return (
    <div className="border-t border-gray-100 bg-white relative z-40">
      <div className="dyntra-container flex items-center gap-1 py-2 overflow-x-auto md:overflow-visible no-scrollbar">
        {/* All Categories Mega Menu */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="all-categories-btn"
              className="flex items-center gap-2 rounded-xl bg-[#4B2AAD] px-4 py-2.5 text-sm font-semibold text-white shrink-0 hover:bg-[#361D82] transition-colors"
            >
              <Menu className="h-4 w-4" />
              All Categories
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-full sm:max-w-[900px] p-0 overflow-hidden">
            <VisuallyHidden>
              <SheetTitle>All Categories</SheetTitle>
              <SheetDescription>Browse the full Dyntra product catalog</SheetDescription>
            </VisuallyHidden>
            <div className="flex h-full">
              {/* L1 rail */}
              <div className="w-64 border-r border-gray-100 bg-[#F8F9FA] thin-scroll overflow-y-auto shrink-0">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-display text-lg font-bold text-gray-900">All Categories</h3>
                  <p className="text-xs text-gray-500">Browse the full Dyntra catalog</p>
                </div>
                {MEGA_CATEGORIES.map((c) => (
                  <button
                    key={c.slug}
                    data-testid={`mega-cat-${c.slug}`}
                    onMouseEnter={() => setActiveCat(c.slug)}
                    onClick={() => setActiveCat(c.slug)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm text-left transition-colors ${activeCat === c.slug ? "bg-white text-[#4B2AAD] font-semibold border-l-4 border-[#4B2AAD]" : "hover:bg-white text-gray-700"}`}
                  >
                    {c.name}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ))}
              </div>
              {/* L2/L3 panel */}
              <div className="flex-1 overflow-y-auto thin-scroll p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-display text-2xl font-bold text-gray-900">{active.name}</h2>
                    <Link
                      to={`/category/${active.slug}`}
                      onClick={() => setSheetOpen(false)}
                      className="text-sm text-[#4B2AAD] font-semibold hover:underline"
                      data-testid={`view-all-${active.slug}`}
                    >
                      View all in {active.name} →
                    </Link>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {active.groups.map((g) => (
                    <div key={g.title}>
                      <h4 className="font-display text-sm font-bold text-gray-900 mb-2 pb-1 border-b border-gray-100">{g.title}</h4>
                      <ul className="space-y-1.5">
                        {g.items.map((it) => (
                          <li key={it}>
                            <Link
                              to={`/category/${active.slug}?sub=${encodeURIComponent(it)}`}
                              onClick={() => setSheetOpen(false)}
                              className="text-sm text-gray-600 hover:text-[#4B2AAD] hover:underline"
                            >
                              {it}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Primary nav links */}
        <nav className="flex items-center gap-1 ml-2">
          {NAV_LINKS.map((n) => {
            if (n.type === "dropdown") {
              const open = openDropdown === n.label;
              return (
                <div key={n.label} className="relative"
                  onMouseEnter={() => setOpenDropdown(n.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    data-testid={`nav-${n.label.toLowerCase()}`}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-gray-700 hover:text-[#4B2AAD] rounded-lg transition-colors"
                  >
                    {n.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="absolute left-0 top-full pt-1 z-50">
                      <div className="w-80 rounded-2xl border border-gray-100 bg-white shadow-xl p-2">
                        {n.items.map((it) => (
                          <Link
                            key={it.label}
                            to={it.href}
                            data-testid={`nav-services-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
                            className="flex flex-col gap-0.5 rounded-xl px-4 py-3 hover:bg-[#F3EFFC] transition-colors"
                          >
                            <span className="text-sm font-semibold text-gray-900">
                              {it.label}
                            </span>
                            <span className="text-xs text-gray-500">{it.desc}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={n.label}
                to={n.href}
                data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${n.highlight ? "text-[#F43F5E] hover:bg-red-50" : "text-gray-700 hover:text-[#4B2AAD]"}`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
